import React, { useState } from 'react';
import axios from 'axios';
import { useAuthUser, useAuthHeader } from 'react-auth-kit';
import { useNavigate } from 'react-router-dom';
import './App.css';

const Register = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState('volunteer');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    const authUser = useAuthUser();
    const authHeader = useAuthHeader();
    const navigate = useNavigate();
    
    // Get current user and check if admin
    const user = authUser();
    const isAdmin = user?.role === 'admin';
    
    // Redirect if not admin
    React.useEffect(() => {
        if (!user || user.role !== 'admin') {
            navigate('/authgrid');
        }
    }, [user, navigate]);
    
    const handleRegister = async () => {
        // Clear previous messages
        setError('');
        setSuccess('');
        
        // Validation
        if (!username.trim() || !password.trim()) {
            setError('Please enter both username and password');
            return;
        }
        
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }
        
        setIsLoading(true);
        
        try {
            // Include auth header for admin endpoints
            const response = await axios.post('/register', {
                username: username.trim(),
                password,
                role
            }, {
                headers: {
                    'Authorization': authHeader()
                }
            });
            
            setSuccess(`User "${username}" registered successfully as ${role}`);
            // Clear form
            setUsername('');
            setPassword('');
            setConfirmPassword('');
            setRole('volunteer');
            
        } catch (err) {
            console.error('Registration error:', err);
            if (err.response?.data?.error) {
                setError(err.response.data.error);
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        handleRegister();
    };
    
    // Don't render if not admin
    if (!isAdmin) {
        return null;
    }
    
    return (
        <div className="fade-in">
            {/* Title Section */}
            <div className="title-style">
                <h1>Register New User</h1>
                <p style={{ fontSize: '14px', margin: '5px 0' }}>
                    Admin Access Only
                </p>
            </div>
            
            {/* Main Registration Form */}
            <div className="main-content-style">
                <div className="player-entry-form">
                    <label className="form-label" htmlFor='username'>Username:</label>
                    <input 
                        className="form-input full-width"
                        type="text" 
                        id="username"
                        placeholder='Enter username' 
                        value={username} 
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={isLoading}
                    />
                    
                    <label className="form-label" htmlFor="password">Password:</label>
                    <input 
                        className="form-input full-width"
                        type="password" 
                        id="password"
                        placeholder='Enter password (min 6 characters)' 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                    />
                    
                    <label className="form-label" htmlFor="confirmPassword">Confirm Password:</label>
                    <input 
                        className="form-input full-width"
                        type="password" 
                        id="confirmPassword"
                        placeholder='Confirm password' 
                        value={confirmPassword} 
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                    />
                    
                    <label className="form-label" htmlFor="role">User Role:</label>
                    <select 
                        className="form-input full-width"
                        id="role"
                        value={role} 
                        onChange={(e) => setRole(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="volunteer">Volunteer</option>
                        <option value="admin">Admin</option>
                        <option value="viewer">Viewer</option>
                    </select>
                    
                    <button 
                        className={isLoading ? 'btn-loading full-width' : 'btn-standard full-width'}
                        type="button" 
                        onClick={handleSubmit}
                        disabled={isLoading}
                        style={{ marginTop: '20px' }}
                    >
                        {isLoading ? 'Registering...' : 'Register User'}
                    </button>
                    
                    <button 
                        className="btn-standard full-width"
                        type="button" 
                        onClick={() => navigate('/authgrid')}
                        disabled={isLoading}
                        style={{ marginTop: '10px' }}
                    >
                        Back to Dashboard
                    </button>
                </div>
                
                {/* Success Message */}
                {success && (
                    <div className="success-message margin-top-medium" style={{ color: 'green' }}>
                        {success}
                    </div>
                )}
                
                {/* Error Message */}
                {error && (
                    <div className="error-message margin-top-medium">
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Register;
